import { Link, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Button } from '@artosera/ui';
import {
  evenementsApi,
  useActionEvenement,
  useEvenements,
  useInscriptionsContact,
  useEvenement,
} from '../../api/evenements.js';
import type { ExpositionPresentee } from '../../api/expositions.js';
import { estApiError } from '../../api/errors.js';
import { galerieALaction } from '../../auth/sujet.js';
import { useShellContext } from '../shell/context.js';
import styles from './Evenements.module.css';

export function EvenementsExposition({ exposition }: { exposition: ExpositionPresentee }) {
  const { t, i18n } = useTranslation(['regie', 'commun']);
  const { galerieSlug, galerie } = useShellContext();
  const navigate = useNavigate();
  const liste = useEvenements(galerieSlug, { expositionId: exposition.id });
  const creation = useActionEvenement(galerieSlug, (_: void, appel) =>
    evenementsApi.creer(
      galerieSlug,
      { type: 'vernissage', expositionId: exposition.id, lieuId: exposition.lieuId },
      appel,
    ),
  );
  const ouvrir =
    exposition.vernissage &&
    liste.isSuccess &&
    !liste.data.evenements.some((e) => e.type === 'vernissage' && e.etat !== 'annule') &&
    galerieALaction(galerie, 'events:write');
  return (
    <section className={styles.carte}>
      <h2>{t('evenements.titre')}</h2>
      {liste.isPending ? <p role="status">{t('commun:chargement')}</p> : null}
      {liste.isError ? <p role="alert">{t('commun:erreurReseau')}</p> : null}
      {liste.data?.evenements.length === 0 ? <p>{t('evenements.aucun')}</p> : null}
      {liste.data?.evenements.map((e) => (
        <p key={e.id}>
          <Link to={`/${galerieSlug}/evenements/${e.id}`}>
            {e.numero} · {e.titre[i18n.language === 'en' ? 'en' : 'fr'] || e.titre.fr}
          </Link>{' '}
          · {t(`evenements.phases.${e.phase}`)}
        </p>
      ))}
      {ouvrir ? (
        <Button
          loading={creation.isPending}
          onClick={() =>
            creation.mutate(undefined, {
              onSuccess: ({ evenement }) => navigate(`/${galerieSlug}/evenements/${evenement.id}`),
            })
          }
        >
          {t('evenements.ouvrirVernissage')}
        </Button>
      ) : null}
      {creation.isError ? (
        <p role="alert">
          {estApiError(creation.error) ? creation.error.message : t('commun:erreurReseau')}
        </p>
      ) : null}
    </section>
  );
}
function LienEvenement({ id }: { id: string }) {
  const { galerieSlug } = useShellContext();
  const { i18n } = useTranslation('regie');
  const fiche = useEvenement(galerieSlug, id);
  const evenement = fiche.data?.evenement;
  let libelle = id;
  if (evenement) {
    const titre = evenement.titre[i18n.language === 'en' ? 'en' : 'fr'] || evenement.titre.fr;
    libelle = `${evenement.numero} · ${titre}`;
  }
  return <Link to={`/${galerieSlug}/evenements/${id}`}>{libelle}</Link>;
}
export function EvenementsContact({ contactId }: { contactId: string }) {
  const { t, i18n } = useTranslation(['regie', 'commun']);
  const { galerieSlug } = useShellContext();
  const liste = useInscriptionsContact(galerieSlug, contactId);
  return (
    <section className={styles.carte}>
      <h2>{t('evenements.titre')}</h2>
      {liste.isPending ? <p role="status">{t('commun:chargement')}</p> : null}
      {liste.isError ? <p role="alert">{t('commun:erreurReseau')}</p> : null}
      {liste.data?.inscriptions.length === 0 ? <p>{t('evenements.aucunInscrit')}</p> : null}
      {liste.data?.inscriptions.map((i) => (
        <p key={i.id}>
          <LienEvenement id={i.evenementId} /> · {t(`evenements.etats.${i.etat}`)} ·{' '}
          {i.presentLe
            ? new Date(i.presentLe).toLocaleString(i18n.language)
            : t('evenements.absent')}
        </p>
      ))}
    </section>
  );
}
